import ProjectDashboard from '../components/ProjectDashboard';
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
  CheckCircle, Circle, Clock, AlertTriangle, Camera, Package, FileText,
  MapPin, Zap, CalendarDays, DollarSign, ArrowLeft, Edit3, Save, X,
  ChevronDown, ChevronUp, Users, AlertCircle, CheckSquare, BarChart3,
  Upload, User, Shield, TrendingUp
} from 'lucide-react';
import toast from 'react-hot-toast';

const tabs = [
  { id: 'overview', label: 'Resumen', icon: FileText },
  { id: 'tasks', label: 'Tareas', icon: CheckSquare },
  { id: 'milestones', label: 'Hitos', icon: CalendarDays },
  { id: 'photos', label: 'Fotos', icon: Camera },
  { id: 'equipment', label: 'Equipos', icon: Package },
  { id: 'team', label: 'Equipo', icon: Users },
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
];

const taskStatusConfig = {
  pending: { icon: Circle, color: '#94A3B8', label: 'Pendiente', next: 'in_progress' },
  in_progress: { icon: Clock, color: '#3B82F6', label: 'En progreso', next: 'completed' },
  completed: { icon: CheckCircle, color: '#10B981', label: 'Completada', next: 'pending' },
  blocked: { icon: AlertTriangle, color: '#EF4444', label: 'Bloqueada', next: 'in_progress' },
};

const milestoneStatusColors = { pending: '#94A3B8', in_progress: '#3B82F6', completed: '#10B981', overdue: '#EF4444' };
const milestoneStatusLabels = { pending: 'Pendiente', in_progress: 'En progreso', completed: 'Completado', overdue: 'Vencido' };

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [tasks, setTasks] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashData, setDashData] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [editingProject, setEditingProject] = useState(false);
  const [projectForm, setProjectForm] = useState({});
  const [users, setUsers] = useState([]);
  const [expandedTask, setExpandedTask] = useState(null);

  const canEdit = ['superadmin', 'admin_empresa', 'project_manager', 'supervisor'].includes(user?.role);

  const loadData = useCallback(async () => {
    try {
      const [projRes, tasksRes, milRes, dashRes] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get('/tasks', { params: { project_id: id } }),
        api.get('/milestones', { params: { project_id: id } }),
        api.get(`/dashboard/project/${id}`),
      ]);
      setProject(projRes.data);
      setProjectForm(projRes.data);
      setTasks(tasksRes.data);
      setMilestones(milRes.data);
      setDashData(dashRes.data);
      if (dashRes.data.recentPhotos) setPhotos(dashRes.data.recentPhotos);
    } catch (e) {
      toast.error('Error cargando proyecto');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    api.get('/users', { params: { limit: 200 } }).then(r => setUsers(r.data.data || [])).catch(() => { });
  }, []);

  // Load equipment when tab is selected
  useEffect(() => {
    if (activeTab === 'equipment' && equipment.length === 0) {
      api.get('/equipment', { params: { project_id: id } }).then(r => setEquipment(r.data)).catch(() => { });
    }
    if (activeTab === 'photos') {
      api.get('/photos', { params: { project_id: id } }).then(r => setPhotos(r.data)).catch(() => { });
    }
  }, [activeTab, id]);

  const cycleTaskStatus = async (task) => {
    if (!canEdit) return;
    const nextStatus = taskStatusConfig[task.status]?.next || 'pending';
    const progress = nextStatus === 'completed' ? 100 : nextStatus === 'in_progress' ? 50 : 0;
    try {
      await api.put(`/tasks/${task.id}`, { status: nextStatus, progressPercentage: progress });
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: nextStatus, progress_percentage: progress } : t));
      // Refresh project progress
      const projRes = await api.get(`/projects/${id}`);
      setProject(projRes.data);
    } catch (e) { toast.error('Error actualizando tarea'); }
  };

  const updateTaskProgress = async (taskId, progress) => {
    try {
      const { data } = await api.put(`/tasks/${taskId}/progress`, { progress: parseInt(progress) });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...data } : t));
      const projRes = await api.get(`/projects/${id}`);
      setProject(projRes.data);
    } catch (e) { toast.error('Error'); }
  };

  const assignTask = async (taskId, userId) => {
    try {
      await api.put(`/tasks/${taskId}`, { assignedTo: userId || null });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, assigned_to: userId, assigned_name: users.find(u => u.id === userId)?.first_name + ' ' + users.find(u => u.id === userId)?.last_name } : t));
      toast.success('Tarea asignada');
    } catch (e) { toast.error('Error'); }
  };

  const updateMilestone = async (msId, updates) => {
    try {
      const { data } = await api.put(`/milestones/${msId}`, updates);
      setMilestones(prev => prev.map(m => m.id === msId ? { ...m, ...data } : m));
      toast.success('Hito actualizado');
      setEditingMilestone(null);
    } catch (e) { toast.error('Error actualizando hito'); }
  };

  const approveMilestone = async (msId) => {
    try {
      const { data } = await api.put(`/milestones/${msId}/approve`);
      setMilestones(prev => prev.map(m => m.id === msId ? { ...m, ...data } : m));
      toast.success('Hito aprobado');
    } catch (e) { toast.error('Error'); }
  };

  const saveProject = async () => {
    try {
      await api.put(`/projects/${id}`, {
        name: projectForm.name,
        description: projectForm.description,
        status: projectForm.status,
        actualStartDate: projectForm.actual_start_date,
        actualEndDate: projectForm.actual_end_date,
      });
      setProject(prev => ({ ...prev, ...projectForm }));
      setEditingProject(false);
      toast.success('Proyecto actualizado');
    } catch (e) { toast.error('Error'); }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!project) return <div className="empty-state"><h3>Proyecto no encontrado</h3></div>;

  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const completedMilestones = milestones.filter(m => m.status === 'completed').length;
  const overdueMilestones = milestones.filter(m => m.deadline_date && new Date(m.deadline_date) < new Date() && m.status !== 'completed').length;
  const totalPaid = milestones.filter(m => m.payment_received).reduce((s, m) => s + parseFloat(m.payment_amount || 0), 0);
  const totalContract = parseFloat(project.contract_value || 0);

  return (
    <div className="page-project-detail">
      <div className="detail-header">
        <button className="btn-back" onClick={() => navigate('/projects')}><ArrowLeft size={20} /> Proyectos</button>
        <div className="detail-title-row">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span className="project-code-lg">{project.code}</span>
              <span className="status-badge" style={{ background: (project.status === 'in_progress' ? '#F59E0B' : '#94A3B8') + '20', color: project.status === 'in_progress' ? '#F59E0B' : '#94A3B8' }}>
                {project.status?.replace('_', ' ')}
              </span>
              {canEdit && !editingProject && (
                <button className="btn-icon" onClick={() => setEditingProject(true)} title="Editar proyecto"><Edit3 size={16} /></button>
              )}
            </div>
            <h1>{editingProject ? <input className="inline-edit-title" value={projectForm.name || ''} onChange={e => setProjectForm(p => ({ ...p, name: e.target.value }))} /> : project.name}</h1>
            <div className="detail-meta">
              {project.city && <span><MapPin size={14} /> {project.city}, {project.country_name}</span>}
              {project.plant_capacity_kwp && <span><Zap size={14} /> {project.plant_capacity_kwp} kWp</span>}
              {project.num_modules && <span>{project.num_modules} módulos</span>}
              {project.pm_name && <span><User size={14} /> PM: {project.pm_name}</span>}
            </div>
            {editingProject && (
              <div className="inline-edit-actions">
                <select value={projectForm.status || ''} onChange={e => setProjectForm(p => ({ ...p, status: e.target.value }))}>
                  <option value="draft">Borrador</option><option value="planning">Planificación</option>
                  <option value="in_progress">En progreso</option><option value="on_hold">En espera</option>
                  <option value="completed">Completado</option>
                </select>
                <button className="btn btn-sm btn-primary" onClick={saveProject}><Save size={14} /> Guardar</button>
                <button className="btn btn-sm btn-secondary" onClick={() => { setEditingProject(false); setProjectForm(project); }}><X size={14} /></button>
              </div>
            )}
          </div>
          <div className="detail-progress-ring">
            <svg viewBox="0 0 80 80">
              <circle cx="40" cy="40" r="34" fill="none" stroke="#1E293B" strokeWidth="6" />
              <circle cx="40" cy="40" r="34" fill="none" stroke="#F59E0B" strokeWidth="6"
                strokeDasharray={`${2 * Math.PI * 34}`}
                strokeDashoffset={`${2 * Math.PI * 34 * (1 - (project.progress_percentage || 0) / 100)}`}
                strokeLinecap="round" transform="rotate(-90 40 40)" />
            </svg>
            <span>{Math.round(project.progress_percentage || 0)}%</span>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {overdueMilestones > 0 && (
        <div className="alert alert-warning">
          <AlertCircle size={18} /> <strong>{overdueMilestones} hito(s) vencido(s)</strong> — Revisa los hitos contractuales.
        </div>
      )}

      <div className="tabs-bar">
        {tabs.map(tab => (
          <button key={tab.id} className={`tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}>
            <tab.icon size={16} /> {tab.label}
            {tab.id === 'milestones' && overdueMilestones > 0 && <span className="tab-badge">{overdueMilestones}</span>}
          </button>
        ))}
      </div>

      <div className="tab-content">
        {/* OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="overview-grid">
            <div className="card">
              <h3>Datos técnicos</h3>
              <div className="info-grid">
                <div className="info-item"><label>Capacidad</label><span>{project.plant_capacity_kwp || '-'} kWp</span></div>
                <div className="info-item"><label>Módulos</label><span>{project.num_modules || '-'} × {project.module_model || '-'}</span></div>
                <div className="info-item"><label>Pot. módulo</label><span>{project.module_power_wp || '-'} Wp</span></div>
                <div className="info-item"><label>Inversores</label><span>{project.num_inverters || '-'} × {project.inverter_model || '-'}</span></div>
                <div className="info-item"><label>Pot. inversor</label><span>{project.inverter_power_kw || '-'} kW</span></div>
                <div className="info-item"><label>Strings</label><span>{project.num_strings || '-'}</span></div>
                <div className="info-item"><label>Estructura</label><span>{project.structure_type || '-'}</span></div>
                <div className="info-item"><label>Transformador</label><span>{project.has_transformer ? (project.transformer_model || 'Sí') : 'No'}</span></div>
                <div className="info-item"><label>Medidor</label><span>{project.meter_model || '-'}</span></div>
                <div className="info-item"><label>PR objetivo</label><span>{project.pr_target}%</span></div>
              </div>
            </div>

            <div className="card">
              <h3><TrendingUp size={16} /> Progreso de tareas</h3>
              <div className="mini-stats">
                <span className="mini-stat green">{completedTasks} completadas</span>
                <span className="mini-stat blue">{tasks.filter(t => t.status === 'in_progress').length} en progreso</span>
                <span className="mini-stat gray">{tasks.filter(t => t.status === 'pending').length} pendientes</span>
                {tasks.filter(t => t.status === 'blocked').length > 0 && <span className="mini-stat red">{tasks.filter(t => t.status === 'blocked').length} bloqueadas</span>}
              </div>
              <div className="task-bars-mini">
                {tasks.slice(0, 8).map(t => (
                  <div key={t.id} className="task-bar-mini">
                    <span className="tbm-name" title={t.name}>{t.name}</span>
                    <div className="tbm-bar"><div className="tbm-fill" style={{ width: `${t.progress_percentage}%`, background: t.color || '#3B82F6' }} /></div>
                    <span className="tbm-pct">{Math.round(t.progress_percentage)}%</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <h3><DollarSign size={16} /> Hitos y facturación</h3>
              <div className="billing-summary">
                <div className="billing-item">
                  <label>Valor contrato</label>
                  <span className="billing-value">{totalContract ? totalContract.toLocaleString() + ' ' + (project.currency || 'EUR') : '-'}</span>
                </div>
                <div className="billing-item">
                  <label>Cobrado</label>
                  <span className="billing-value green">{totalPaid.toLocaleString()} {project.currency || 'EUR'}</span>
                </div>
                <div className="billing-item">
                  <label>Pendiente</label>
                  <span className="billing-value amber">{(totalContract - totalPaid).toLocaleString()} {project.currency || 'EUR'}</span>
                </div>
              </div>
              <div className="milestone-progress-bar">
                <div className="mpb-fill" style={{ width: `${totalContract ? (totalPaid / totalContract * 100) : 0}%` }} />
              </div>
              <span className="mpb-label">{totalContract ? Math.round(totalPaid / totalContract * 100) : 0}% facturado</span>
            </div>
          </div>
        )}

        {/* TASKS */}
        {activeTab === 'tasks' && (
          <div className="tasks-section">
            <div className="section-header">
              <h3>{completedTasks}/{tasks.length} tareas completadas</h3>
              <div className="progress-bar" style={{ width: 200 }}>
                <div className="progress-fill" style={{ width: `${tasks.length ? (completedTasks / tasks.length * 100) : 0}%` }} />
              </div>
            </div>
            <div className="tasks-list">
              {tasks.map(task => {
                const cfg = taskStatusConfig[task.status] || taskStatusConfig.pending;
                const Icon = cfg.icon;
                const isExpanded = expandedTask === task.id;
                return (
                  <div key={task.id} className={`task-row ${isExpanded ? 'expanded' : ''}`}>
                    <div className="task-main" onClick={() => setExpandedTask(isExpanded ? null : task.id)}>
                      <button className="task-status-btn" style={{ color: cfg.color }}
                        onClick={(e) => { e.stopPropagation(); cycleTaskStatus(task); }}
                        title={`${cfg.label} → clic para cambiar`}>
                        <Icon size={22} />
                      </button>
                      <div className="task-info">
                        <div className="task-name-row">
                          <span className="task-name">{task.name}</span>
                          {task.is_contractual && <span className="contractual-badge"><Shield size={10} /> Contractual</span>}
                        </div>
                        <div className="task-progress-bar">
                          <div className="fill" style={{ width: `${task.progress_percentage}%`, background: task.color || cfg.color }} />
                        </div>
                      </div>
                      <span className="task-pct" style={{ color: cfg.color }}>{Math.round(task.progress_percentage)}%</span>
                      {task.assigned_name && <span className="task-assigned"><User size={12} /> {task.assigned_name}</span>}
                      <ChevronDown size={16} className={`task-chevron ${isExpanded ? 'rotated' : ''}`} />
                    </div>

                    {isExpanded && canEdit && (
                      <div className="task-expanded">
                        <div className="task-expanded-row">
                          <label>Progreso</label>
                          <input type="range" min="0" max="100" step="5" value={task.progress_percentage}
                            onChange={e => {
                              const val = parseInt(e.target.value);
                              setTasks(prev => prev.map(t => t.id === task.id ? { ...t, progress_percentage: val } : t));
                            }}
                            onMouseUp={e => updateTaskProgress(task.id, e.target.value)}
                            onTouchEnd={e => updateTaskProgress(task.id, e.target.value)} />
                          <span>{Math.round(task.progress_percentage)}%</span>
                        </div>
                        <div className="task-expanded-row">
                          <label>Asignar a</label>
                          <select value={task.assigned_to || ''} onChange={e => assignTask(task.id, e.target.value)}>
                            <option value="">Sin asignar</option>
                            {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                          </select>
                        </div>
                        <div className="task-expanded-row">
                          <label>Estado</label>
                          <div className="status-buttons">
                            {Object.entries(taskStatusConfig).map(([key, val]) => (
                              <button key={key} className={`status-btn ${task.status === key ? 'active' : ''}`}
                                style={{ '--btn-color': val.color }} onClick={() => cycleTaskStatus({ ...task, status: key === task.status ? key : Object.keys(taskStatusConfig)[Object.keys(taskStatusConfig).indexOf(key) - 1] || 'pending' })}>
                                <val.icon size={14} /> {val.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* MILESTONES */}
        {activeTab === 'milestones' && (
          <div className="milestones-section">
            <div className="section-header">
              <h3>{completedMilestones}/{milestones.length} hitos completados</h3>
              {overdueMilestones > 0 && <span className="alert-badge"><AlertCircle size={14} /> {overdueMilestones} vencidos</span>}
            </div>
            <div className="milestones-timeline">
              {milestones.map((ms, idx) => {
                const isOverdue = ms.deadline_date && new Date(ms.deadline_date) < new Date() && ms.status !== 'completed';
                const isEditing = editingMilestone === ms.id;
                return (
                  <div key={ms.id} className={`milestone-timeline-item ${ms.status} ${isOverdue ? 'overdue' : ''}`}>
                    <div className="mt-connector">
                      <div className="mt-dot" style={{ background: isOverdue ? '#EF4444' : milestoneStatusColors[ms.status] }} />
                      {idx < milestones.length - 1 && <div className="mt-line" />}
                    </div>
                    <div className="mt-content">
                      <div className="mt-header">
                        <h4>{ms.name}</h4>
                        <div className="mt-badges">
                          <span className="mt-status" style={{ color: isOverdue ? '#EF4444' : milestoneStatusColors[ms.status] }}>
                            {isOverdue ? '⚠ Vencido' : milestoneStatusLabels[ms.status]}
                          </span>
                          <span className="mt-payment">{ms.payment_percentage}% — {ms.payment_amount ? Number(ms.payment_amount).toLocaleString() : '0'} {project.currency || 'EUR'}</span>
                        </div>
                      </div>

                      <div className="mt-details">
                        {ms.planned_date && <span><CalendarDays size={12} /> Plan: {new Date(ms.planned_date).toLocaleDateString('es')}</span>}
                        {ms.deadline_date && <span>Límite: {new Date(ms.deadline_date).toLocaleDateString('es')}</span>}
                        {ms.actual_date && <span className="green">✓ Completado: {new Date(ms.actual_date).toLocaleDateString('es')}</span>}
                        {ms.invoice_number && <span>Factura: {ms.invoice_number}</span>}
                        <span className={ms.payment_received ? 'green' : 'amber'}>
                          {ms.payment_received ? '✓ Cobrado' : '○ Pendiente cobro'}
                        </span>
                      </div>

                      {canEdit && (
                        <div className="mt-actions">
                          {ms.status !== 'completed' && (
                            <button className="btn btn-sm btn-success" onClick={() => updateMilestone(ms.id, { status: 'completed', actualDate: new Date().toISOString().split('T')[0] })}>
                              <CheckCircle size={14} /> Completar
                            </button>
                          )}
                          {ms.status === 'completed' && !ms.approved_by && (
                            <button className="btn btn-sm btn-primary" onClick={() => approveMilestone(ms.id)}>
                              <Shield size={14} /> Aprobar
                            </button>
                          )}
                          {ms.approved_by && <span className="approved-badge"><CheckCircle size={12} /> Aprobado por {ms.approved_by_name}</span>}

                          <button className="btn btn-sm btn-secondary" onClick={() => setEditingMilestone(isEditing ? null : ms.id)}>
                            <Edit3 size={14} /> {isEditing ? 'Cerrar' : 'Editar'}
                          </button>
                        </div>
                      )}

                      {isEditing && (
                        <div className="mt-edit-form">
                          <div className="form-row">
                            <div className="form-group">
                              <label>Fecha límite</label>
                              <input type="date" defaultValue={ms.deadline_date?.split('T')[0]} onChange={e => ms._deadlineDate = e.target.value} />
                            </div>
                            <div className="form-group">
                              <label>Nº Factura</label>
                              <input defaultValue={ms.invoice_number || ''} onChange={e => ms._invoiceNumber = e.target.value} />
                            </div>
                          </div>
                          <div className="form-row">
                            <div className="form-group">
                              <label>Fecha factura</label>
                              <input type="date" defaultValue={ms.invoice_date?.split('T')[0]} onChange={e => ms._invoiceDate = e.target.value} />
                            </div>
                            <div className="form-group checkbox-group">
                              <label>
                                <input type="checkbox" defaultChecked={ms.payment_received} onChange={e => ms._paymentReceived = e.target.checked} />
                                <span>Pago recibido</span>
                              </label>
                            </div>
                          </div>
                          <div className="form-group">
                            <label>Notas de evidencia</label>
                            <textarea rows={2} defaultValue={ms.evidence_notes || ''} onChange={e => ms._evidenceNotes = e.target.value} />
                          </div>
                          <button className="btn btn-sm btn-primary" onClick={() => updateMilestone(ms.id, {
                            deadlineDate: ms._deadlineDate, invoiceNumber: ms._invoiceNumber,
                            invoiceDate: ms._invoiceDate, paymentReceived: ms._paymentReceived,
                            evidenceNotes: ms._evidenceNotes
                          })}><Save size={14} /> Guardar cambios</button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* PHOTOS */}
        {activeTab === 'photos' && (
          <div className="photos-section">
            <div className="photos-grid">
              {photos.map(photo => (
                <div key={photo.id} className="photo-card">
                  <img src={photo.thumbnail_url || photo.file_url} alt={photo.description || 'Foto'} loading="lazy" />
                  <div className="photo-overlay">
                    {photo.photo_type && <span className="photo-type-badge">{photo.photo_type}</span>}
                    {photo.barcode_data && <span className="photo-tag">📊 {photo.barcode_data}</span>}
                    {photo.serial_number && <span className="photo-tag">🔢 {photo.serial_number}</span>}
                    {photo.latitude && <span className="photo-tag">📍 {Number(photo.latitude).toFixed(4)}, {Number(photo.longitude).toFixed(4)}</span>}
                  </div>
                  <div className="photo-meta">
                    <span>{photo.uploaded_by_name}</span>
                    <span>{new Date(photo.created_at).toLocaleDateString('es')}</span>
                    {photo.is_verified && <span className="verified-badge">✓</span>}
                  </div>
                </div>
              ))}
              {photos.length === 0 && <div className="empty-state"><Camera size={48} /><h3>No hay fotos</h3><p>Sube fotos desde la app móvil con geolocalización y código de barras.</p></div>}
            </div>
          </div>
        )}

        {/* EQUIPMENT */}
        {activeTab === 'equipment' && (
          <div className="equipment-section">
            {equipment.length > 0 ? (
              <table className="data-table">
                <thead><tr><th>Tipo</th><th>Marca/Modelo</th><th>Serie</th><th>Barcode</th><th>Estado</th><th>Ubicación</th><th>Instalado por</th></tr></thead>
                <tbody>
                  {equipment.map(eq => (
                    <tr key={eq.id}>
                      <td><span className="equip-type">{eq.type}</span></td>
                      <td>{eq.brand} {eq.model}</td>
                      <td className="mono">{eq.serial_number || '-'}</td>
                      <td className="mono">{eq.barcode || '-'}</td>
                      <td><span className={`status-pill ${eq.status}`}>{eq.status}</span></td>
                      <td>{[eq.location_zone, eq.location_string, eq.location_row].filter(Boolean).join(' / ') || '-'}</td>
                      <td>{eq.installed_by_name || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state"><Package size={48} /><h3>No hay equipos registrados</h3><p>Los equipos se registran mediante escaneo de código de barras desde la app móvil.</p></div>
            )}
          </div>
        )}

        {/* TEAM */}
        {activeTab === 'team' && (
          <div className="team-section">
            <div className="team-grid">
              {(project.members || []).map(m => (
                <div key={m.id} className="team-card">
                  <div className="team-avatar">{m.first_name?.[0]}{m.last_name?.[0]}</div>
                  <div>
                    <span className="team-name">{m.first_name} {m.last_name}</span>
                    <span className="team-email">{m.email}</span>
                    <span className="role-badge">{m.user_role}</span>
                  </div>
                </div>
              ))}
              {(!project.members || project.members.length === 0) && (
                <div className="empty-state"><Users size={48} /><p>No hay miembros asignados</p></div>
              )}
            </div>
          </div>
        )}

        {/* DASHBOARD */}
        {activeTab === 'dashboard' && (
          <ProjectDashboard projectId={id} project={project} />
        )}
      </div>
    </div>
  );
}
